import { IOption } from '../../../../base';
import { IInterviewAvailability } from '../../../../common';
import { TFunction } from 'i18next';

const formatTime = (totalMinutes: number, language: string): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const minuteStr = minutes.toString().padStart(2, '0');

    // Use 24-hour format for Hebrew and other RTL languages
    if (language.toLowerCase() === 'he' || language.toLowerCase() === 'he-il') {
        return `${hours.toString().padStart(2, '0')}:${minuteStr}`;
    }

    // Use 12-hour format with AM/PM for English
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours < 12 ? 'AM' : 'PM';
    return `${displayHour}:${minuteStr} ${period}`;
};

// Generate interview duration options
// 15 min intervals up to 2.5 hours (150 minutes)
// Then 30 min intervals up to 8 hours (480 minutes)
export const generateDurationOptions = (
    t: TFunction,
    language: string
): IOption[] => {
    const options: IOption[] = [];
    const isRTL =
        language.toLowerCase() === 'he' ||
        language.toLowerCase() === 'he-il';

    // 15 minute intervals from 15 to 150 minutes (2.5 hours)
    for (let minutes = 15; minutes <= 150; minutes += 15) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        let label = '';
        if (hours > 0 && mins > 0) {
            const hourLabel =
                hours > 1
                    ? t('scheduleInterview.hours')
                    : t('scheduleInterview.hour');
            const minuteLabel = t('scheduleInterview.minutes');
            // For RTL: number + unit is natural, use LRM to prevent reversal
            label = isRTL
                ? `${hours} ${hourLabel} ${mins} ${minuteLabel}\u200E`
                : `${hours} ${hourLabel} ${mins} ${minuteLabel}`;
        } else if (hours > 0) {
            const hourLabel =
                hours > 1
                    ? t('scheduleInterview.hours')
                    : t('scheduleInterview.hour');
            label = isRTL
                ? `${hours} ${hourLabel}\u200E`
                : `${hours} ${hourLabel}`;
        } else {
            const minuteLabel = t('scheduleInterview.minutes');
            label = isRTL
                ? `${mins} ${minuteLabel}\u200E`
                : `${mins} ${minuteLabel}`;
        }

        options.push({
            label,
            value: minutes.toString(),
        });
    }

    // 30 minute intervals from 180 to 480 minutes (3 to 8 hours)
    for (let minutes = 180; minutes <= 480; minutes += 30) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        let label = '';
        const hourLabel = t('scheduleInterview.hours');
        if (hours > 0 && mins > 0) {
            const minuteLabel = t('scheduleInterview.minutes');
            label = isRTL
                ? `${hours} ${hourLabel} ${mins} ${minuteLabel}\u200E`
                : `${hours} ${hourLabel} ${mins} ${minuteLabel}`;
        } else {
            label = isRTL
                ? `${hours} ${hourLabel}\u200E`
                : `${hours} ${hourLabel}`;
        }

        options.push({
            label,
            value: minutes.toString(),
        });
    }

    return options;
};

export interface IAvailabilityDisplayItem {
    day: string;
    timeRanges: string;
}

const DAY_TRANSLATION_KEYS = [
    'sundayFull',
    'mondayFull',
    'tuesdayFull',
    'wednesdayFull',
    'thursdayFull',
    'fridayFull',
    'saturdayFull',
];

export const formatAvailabilityForDisplay = (
    availability: IInterviewAvailability,
    language: string,
    t: TFunction
): IAvailabilityDisplayItem[] => {
    const dayOrder = [0, 1, 2, 3, 4, 5, 6];

    return dayOrder
        .filter((dayIndex) => availability[dayIndex]?.length > 0)
        .map((dayIndex) => {
            const dayName = t(
                `scheduleInterview.${DAY_TRANSLATION_KEYS[dayIndex]}`
            );
            const windows = availability[dayIndex];
            const timeRanges = windows
                .map((w) => {
                    const from = formatTime(parseInt(w.from), language);
                    const to = formatTime(parseInt(w.to), language);
                    return `${from}\u2013${to}`;
                })
                .join(', ');

            return { day: dayName, timeRanges };
        });
};

// Generate time options with 30 minute intervals from 8am to 8pm
export const generateTimeOptions = (language: string): IOption[] => {
    const options: IOption[] = [];

    // Start at 8am (hour 8) and end at 8pm (hour 20)
    for (let hour = 8; hour <= 20; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
            // Skip 8:30 PM onwards
            if (hour === 20 && minute > 0) break;

            // Store value as minutes from midnight for easier comparison
            const value = hour * 60 + minute;
            const label = formatTime(value, language);

            options.push({
                label,
                value: value.toString(),
            });
        }
    }

    return options;
};
